// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  AdvisoryDebtAccount,
  AdvisoryDebtCreditLine,
  AdvisoryDebtLoan,
} from '@shared/data-access/types/brokerage-types';
import {
  getPendingDebtAmortizations,
  resolveInstitutionLabel,
} from './debt-amortizations';
import { isDefined } from '@shared/utils';

export type NextAmortizationLabelKey =
  | 'amortization-monthly-installment'
  | 'amortization-line-maturity';

export type NextAmortizationItem = {
  id: string;
  labelKey: NextAmortizationLabelKey;
  labelParams: { name: string; bank?: string };
  date: string | null;
  amount: number;
};

const parseDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);

  return new Date(year!, month! - 1, day);
};

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isWithinWindow = (
  dateString: string,
  windowDays: number,
  referenceDate: Date,
): boolean => {
  const itemDate = startOfDay(parseDate(dateString));
  const today = startOfDay(referenceDate);
  const windowEnd = new Date(today);

  windowEnd.setDate(windowEnd.getDate() + windowDays);

  return itemDate >= today && itemDate <= windowEnd;
};

const sortNextAmortizationItems = (
  items: NextAmortizationItem[],
): NextAmortizationItem[] =>
  [...items].sort((left, right) => {
    if (left.date === null && right.date === null) {
      return 0;
    }

    if (left.date === null) {
      return 1;
    }

    if (right.date === null) {
      return -1;
    }

    return parseDate(left.date).getTime() - parseDate(right.date).getTime();
  });

const passesWindowFilter = (
  item: NextAmortizationItem,
  windowDays: number,
  referenceDate: Date,
): boolean => {
  if (item.date === null) {
    return true;
  }

  return isWithinWindow(item.date, windowDays, referenceDate);
};

const buildLoanAmortizationItems = (
  loan: AdvisoryDebtLoan,
  bankName: string,
  referenceDate: Date,
): NextAmortizationItem[] => {
  const productName = loan.name ?? '-';
  const pendingAmortizations = getPendingDebtAmortizations(loan.amortizations);
  const datedPending = pendingAmortizations.filter(
    entry => entry.installment_date !== null,
  );
  const undatedPending = pendingAmortizations.filter(
    entry => entry.installment_date === null,
  );
  const today = startOfDay(referenceDate);

  const futureDatedPending = datedPending.filter(entry =>
    entry.installment_date
      ? startOfDay(parseDate(entry.installment_date)) >= today
      : false,
  );

  const datedItems = futureDatedPending.map((entry, entryIndex) => ({
    id: `loan-${loan.provider_uuid}-${entry.installment_date}-${entryIndex}`,
    labelKey: 'amortization-monthly-installment' as const,
    labelParams: { name: productName, bank: bankName },
    date: entry.installment_date,
    amount: entry.total,
  }));

  const undatedItems = undatedPending.map((entry, entryIndex) => ({
    id: `loan-${loan.provider_uuid}-undated-${entryIndex}`,
    labelKey: 'amortization-monthly-installment' as const,
    labelParams: { name: productName, bank: bankName },
    date: null,
    amount: entry.total,
  }));

  const items = [...datedItems, ...undatedItems];

  if (items.length === 0 && isDefined(loan.next_installment)) {
    items.push({
      id: `loan-${loan.provider_uuid}-next-installment`,
      labelKey: 'amortization-monthly-installment',
      labelParams: { name: productName, bank: bankName },
      date: null,
      amount: loan.next_installment,
    });
  }

  return items;
};

const buildCreditLineAmortizationItems = (
  creditLine: AdvisoryDebtCreditLine,
  bankName: string,
): NextAmortizationItem[] => {
  const productName = creditLine.name ?? creditLine.product_type ?? '-';
  const pendingAmortizations = getPendingDebtAmortizations(
    creditLine.amortizations,
  );
  const datedPending = pendingAmortizations.filter(
    entry => entry.installment_date !== null,
  );
  const undatedPending = pendingAmortizations.filter(
    entry => entry.installment_date === null,
  );
  const lastDatedIndex = datedPending.length - 1;

  const datedItems = datedPending.map((entry, entryIndex) => ({
    id: `cl-${creditLine.provider_uuid}-${entry.installment_date}-${entryIndex}`,
    labelKey:
      entryIndex === lastDatedIndex && lastDatedIndex >= 0
        ? ('amortization-line-maturity' as const)
        : ('amortization-monthly-installment' as const),
    labelParams:
      entryIndex === lastDatedIndex && lastDatedIndex >= 0
        ? { name: bankName, bank: bankName }
        : { name: productName, bank: bankName },
    date: entry.installment_date,
    amount: entry.total,
  }));

  const undatedItems = undatedPending.map((entry, entryIndex) => ({
    id: `cl-${creditLine.provider_uuid}-undated-${entryIndex}`,
    labelKey: 'amortization-monthly-installment' as const,
    labelParams: { name: productName, bank: bankName },
    date: null,
    amount: entry.total,
  }));

  return [...datedItems, ...undatedItems];
};

export const buildNextAmortizations = (
  accounts: AdvisoryDebtAccount[] = [],
  windowDays: number,
  institutionNames: Map<string, string> = new Map(),
  referenceDate = new Date(),
): NextAmortizationItem[] => {
  const items = accounts.flatMap(account => {
    const bankName = resolveInstitutionLabel(
      account.institution_id,
      institutionNames,
    );

    const loanItems = (account.loans ?? []).flatMap(loan =>
      buildLoanAmortizationItems(loan, bankName, referenceDate),
    );

    const creditLineItems = (account.credit_lines ?? []).flatMap(creditLine =>
      buildCreditLineAmortizationItems(creditLine, bankName),
    );

    return [...loanItems, ...creditLineItems];
  });

  return sortNextAmortizationItems(
    items.filter(item => passesWindowFilter(item, windowDays, referenceDate)),
  );
};

export const hasAmortizationProducts = (
  accounts: AdvisoryDebtAccount[] = [],
): boolean =>
  accounts.some(
    account =>
      (account.loans?.length ?? 0) > 0 || (account.credit_lines?.length ?? 0) > 0,
  );
